// src/pages/Platform/PlatformNotifications.js
//
// The Super Admin panel's own notification feed — handlers.
// ListPlatformNotificationsHandler/MarkPlatformNotificationReadHandler/
// MarkAllPlatformNotificationsReadHandler (task #202). A plain page rather
// than components/UI/NotificationBell.jsx's dropdown-off-a-bell-icon (that
// component is tenant-only — see Companies.js's own doc comment on why
// components/UI/* isn't reused in the Platform tree at all).
import { useState, useEffect, useCallback, useContext } from "react";
import {
  getPlatformNotifications,
  markPlatformNotificationRead,
  markAllPlatformNotificationsRead,
} from "../../services/api";
import { PlatformAuthContext } from "../../context/PlatformAuthContext";

const TYPE_LABELS = {
  subscription_reminder: "Renewal reminder",
  subscription_grace: "Grace period started",
  subscription_suspended: "Company suspended",
  provisioning_failed: "Provisioning failed",
};

export default function PlatformNotifications() {
  const { token } = useContext(PlatformAuthContext);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getPlatformNotifications(token);
      setNotifications(res || []);
    } catch (err) {
      console.error("Failed to load notifications:", err.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const handleMarkRead = async (id) => {
    setNotifications((list) => list.map((n) => (n.id === id ? { ...n, is_read: true } : n)));
    try {
      await markPlatformNotificationRead(token, id);
    } catch (err) {
      console.error("Failed to mark notification read:", err.message);
    }
  };

  const handleMarkAllRead = async () => {
    setNotifications((list) => list.map((n) => ({ ...n, is_read: true })));
    try {
      await markAllPlatformNotificationsRead(token);
    } catch (err) {
      console.error("Failed to mark all notifications read:", err.message);
    }
  };

  const unreadCount = notifications.filter((n) => !n.is_read).length;

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <h2 style={{ margin: 0, fontSize: 20, color: "#0f172a" }}>Notifications</h2>
        {unreadCount > 0 && (
          <button style={secondaryBtnStyle} onClick={handleMarkAllRead}>
            Mark all read ({unreadCount})
          </button>
        )}
      </div>

      <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", overflow: "hidden" }}>
        {loading ? (
          <div style={emptyStyle}>Loading...</div>
        ) : notifications.length === 0 ? (
          <div style={emptyStyle}>No notifications yet.</div>
        ) : (
          notifications.map((n) => (
            <div
              key={n.id}
              style={{
                ...rowStyle,
                background: n.is_read ? "#fff" : "#f0f9ff",
              }}
            >
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#0284c7", textTransform: "uppercase" }}>
                  {TYPE_LABELS[n.type] || n.type}
                </div>
                <div style={{ fontWeight: 600, color: "#0f172a", fontSize: 14, marginTop: 2 }}>{n.title}</div>
                <div style={{ fontSize: 13, color: "#64748b", marginTop: 2 }}>{n.message}</div>
                <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 4 }}>
                  {new Date(n.created_at).toLocaleString()}
                </div>
              </div>
              {!n.is_read && (
                <button style={linkBtnStyle} onClick={() => handleMarkRead(n.id)}>
                  Mark read
                </button>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

const rowStyle = { display: "flex", alignItems: "flex-start", gap: 12, padding: "14px 16px", borderTop: "1px solid #f1f5f9" };
const emptyStyle = { padding: 40, textAlign: "center", color: "#94a3b8" };
const secondaryBtnStyle = { background: "#fff", color: "#334155", border: "1px solid #cbd5e1", borderRadius: 8, padding: "8px 14px", fontSize: 13, cursor: "pointer" };
const linkBtnStyle = { background: "none", border: "none", color: "#2563eb", fontSize: 13, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" };
